<template>
    <div
        class="store-locator-container d-flex flex-column"
        :class="{
            'full-screen': isMapExpanded,
            'text-grey-lighten-4': isMapExpanded
        }"
    >
        <form
            @submit="onSubmit($event)"
            @reset="onReset($event)"
            class="flex-grow-0 mb-4"
            novalidate
        >
            <v-expansion-panels
                v-model="panelSearchBox"
            >
                <v-expansion-panel
                    :elevation="0"
                    class="border-thin rounded-lg"
                >
                    <v-expansion-panel-title
                        class="font-weight-medium text-body-large"
                        static
                    >
                        Search Map
                    </v-expansion-panel-title>
                    <v-expansion-panel-text
                        class="pt-3"
                    >
                        <v-row
                            :density="!isMobile ? 'compact' : 'default'"
                        >
                            <!-- Sport -->
                            <v-col
                                cols="12"
                                md="4"
                            >
                                <v-select
                                    v-model="markets"
                                    v-bind="marketsProps"
                                    name="sport"
                                    label="Sport"
                                    :items="Array.from(marketsFilterItems.values())"
                                    placeholder="All Sports"
                                    density="compact"
                                    persistent-placeholder
                                    hide-details
                                >
                                    <template #prepend-item>
                                        <v-list-item
                                            title=""
                                            @click="markets = null"
                                        >
                                            <v-list-item-title>
                                                All Sports
                                            </v-list-item-title>
                                        </v-list-item>
                                    </template>
                                </v-select>
                            </v-col>

                            <!-- Location -->
                            <v-col
                                cols="12"
                                md="4"
                            >
                                <v-text-field
                                    v-model="location"
                                    v-bind="locationProps"
                                    name="location"
                                    label="Location"
                                    placeholder="City, State, Country"
                                    density="compact"
                                    persistent-placeholder
                                    hide-details
                                ></v-text-field>
                            </v-col>

                            <!-- Radius -->
                            <v-col
                                cols="6"
                                md="2"
                            >
                                <v-select
                                    v-model="radius"
                                    v-bind="radiusProps"
                                    name="itemRadius"
                                    label="Radius"
                                    :items="itemsRadius"
                                    item-title="text"
                                    density="compact"
                                    hide-details
                                ></v-select>
                            </v-col>

                            <!-- Unit -->
                            <v-col
                                cols="6"
                                md="2"
                            >
                                <v-select
                                    v-model="unit"
                                    v-bind="unitProps"
                                    name="unit"
                                    label="Unit"
                                    item-title="text"
                                    :items="itemsUnit"
                                    density="compact"
                                    hide-details
                                ></v-select>
                            </v-col>
                        </v-row>

                        <!-- Submit / Reset -->
                        <v-row
                            density="compact"
                        >
                            <v-col
                                cols="12"
                                class="d-flex justify-end"
                            >
                                <div
                                    class="action-buttons d-flex gap-2"
                                >
                                    <v-btn
                                        class="flex-shrink-1 flex-grow-0"
                                        color="secondary"
                                        variant="text"
                                        type="reset"
                                    >
                                        Reset
                                    </v-btn>
                                    <v-btn
                                        class="flex-shrink-0 flex-grow-1"
                                        color="secondary"
                                        type="submit"
                                        variant="flat"
                                        :disabled="!formMeta.valid"
                                    >
                                        Search
                                    </v-btn>
                                </div>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
        </form>

        <div
            class="map-panel d-flex flex-column flex-grow-1"
        >
            <v-toolbar
                class="mb-0 flex-grow-0"
                elevation="0"
                density="compact"
            >
                <v-btn
                    color="secondary"
                    @click="retailerListPanel = !retailerListPanel"
                    class="show-list-btn"
                    variant="text"
                    aria-label="Show/Hide List"
                >
                    <v-icon
                        class="mr-2"
                        :icon="mdiFormatListBulletedSquare"
                    ></v-icon>
                    <template
                        v-if="!isMobile"
                    >
                        <span
                            v-if="retailerListPanel"
                        >
                            Hide
                        </span>
                        <span
                            v-else
                        >
                            Show
                        </span>
                        &nbsp;List
                    </template>
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                    color="secondary"
                    @click="toggleFullScreen()"
                    class="toggle-fullscreen-btn"
                    variant="text"
                    aria-label="Toggle Fullscreen"
                    :aria-expanded="isMapExpanded"
                >
                    <template v-if="!isMapExpanded">
                        <v-icon class="mr-2" :icon="mdiArrowExpandAll"></v-icon>
                        <template v-if="!isMobile">Expand Map</template>
                    </template>
                    <template v-else>
                        <v-icon class="mr-2" :icon="mdiArrowCollapseAll"></v-icon>
                        <template v-if="!isMobile">Return</template>
                    </template>
                </v-btn>
            </v-toolbar>

            <div
                class="position-relative overflow-hidden flex-grow-1 flex-shrink-0 rounded-t-0"
            >
                <v-navigation-drawer
                    v-model="retailerListPanel"
                    width="320"
                    location="left"
                    :scrim="false"
                    disable-resize-watcher
                    disable-route-watcher
                    absolute
                    temporary
                >
                    <template #prepend>
                        <div class="py-2 position-relative">
                            <div class="text-center font-weight-bold">
                                <!-- TODO: Consider showing error if items don't load? -->
                                <span
                                    v-if="isFiltering"
                                >
                                    Showing {{ filteredLocationsCount }} of
                                </span>
                                <span>{{ locationsCount }} stores</span>

                                <v-btn
                                    v-if="isFiltering"
                                    :icon="mdiFilterRemove"
                                    color="secondary"
                                    variant="plain"
                                    size="small"
                                    @click="onReset($event)"
                                ></v-btn>
                            </div>
                            <v-text-field
                                placeholder="Filter Stores"
                                v-model="retailLocationsFilter"
                                class="ma-4 white"
                                variant="outlined"
                                density="compact"
                                hide-details
                                clearable
                            ></v-text-field>
                            <v-btn
                                @click="retailerListPanel = false"
                                class="ma-auto position-absolute"
                                style="top: 0; right: 0;"
                                variant="plain"
                                size="small"
                                color="secondary"
                                :ripple="false"
                                aria-label="Close Retailer List"
                                :icon="mdiWindowClose"
                            ></v-btn>
                        </div>
                    </template>
                    <template #default>
                        <v-virtual-scroll
                            v-if="!lo_isError(retailLocations) && locationsCount > 0"
                            class="list-retailers"
                            :items="retailLocationsFiltered"
                            height="100%"
                            item-height="180"
                        >
                            <template #default="{ item, index }">
                                <v-list-item :key="item.uid">
                                    <template v-if="item.address">
                                        <v-list-item-title>
                                            <strong>{{ item.address.company }}</strong>
                                        </v-list-item-title>
                                        <address
                                            class="text-body-large"
                                        >
                                            {{ item.address.street1 }}
                                            <br />
                                            <template
                                                v-if="item.address.street2"
                                            >
                                                {{ item.address.street2 }}
                                                <br />
                                            </template>
                                            {{ item.address.city }} {{ item.address.state }}, {{ item.address.postalCode }}
                                            <br />
                                            {{ item.address.country }}
                                        </address>
                                        <div
                                            v-if="item.phone"
                                            class="my-2"
                                        >
                                            <a
                                                :href="`tel:${item.phone}`"
                                            >
                                                {{ item.phone }}
                                            </a>
                                        </div>
                                        <div
                                            v-if="item.website"
                                            class="my-2 text-truncate"
                                        >
                                            <a
                                                :href="`${item.website}`"
                                                target="_blank"
                                                class="anchor"
                                            >
                                                Website
                                            </a>
                                        </div>
                                        <ul
                                            v-if="item.sportGroups?.length"
                                            class="my-2 px-1 list-inside"
                                        >
                                            <li
                                                v-for="(group, index) in sortSportGroups(item.sportGroups)"
                                                :key="`store-location-list-sport-group-${index}`"
                                            >
                                                {{ sportGroupLabel(group) }}
                                            </li>
                                        </ul>
                                    </template>
                                    <template
                                        #append
                                        v-if="item.address.latitude && item.address.longitude"
                                    >
                                        <v-btn
                                            @click="locationItemClick(item)"
                                            variant="plain"
                                            icon
                                        >
                                            <v-icon
                                                :icon="mdiMapMarkerRight"
                                                color="secondary"
                                            ></v-icon>
                                        </v-btn>
                                    </template>
                                </v-list-item>
                            </template>
                        </v-virtual-scroll>
                    </template>
                </v-navigation-drawer>
                <client-only>
                    <LocationsMap
                        ref="elLocationsMap"
                        class="cmp-address-map fill-available-height"
                        v-model:markets-filter-list="marketsFilterItems"
                        :height="mapHeight"
                        :locations="retailLocations"
                        :search="searchObj"
                        @on-bound-locations="onBoundLocations($event)"
                        @location-search-error="showLocationSearchError()"
                    ></LocationsMap>
                </client-only>
            </div>
        </div>

        <DebugContent>
            <div class="py-4">
                <v-btn @click="getRetailLocations()" color="primary" small>Reload Locations</v-btn>
            </div>
        </DebugContent>
    </div>
</template>

<script lang="ts" setup>
    import LocationsMap from '~/components/LocationsMap/LocationsMap.vue';
    import type {
        LocationsMapSearch,
        SelectListItem,
        Location
    } from '~/types';
    import type {
        GenericVuetifyListItem
    } from 'x1-base/types';
    import { LocationsMapSearchModel, LocationsMapSearchSchema } from '~/models';
    import { useLocations, useUiState, useIsMobile, useSportGroups } from '~/composables';
    import { useGlobalAlert } from 'x1-base/composables';
    import { useFormValidation } from 'x1-base/composables';
    import { isError as lo_isError } from 'es-toolkit';
    import { isNil as lo_isNil } from 'es-toolkit';

    import { merge as lo_mergeCompat } from 'es-toolkit/compat';
    import DebugContent from '~/components/DebugContent/DebugContent.vue';
    import { VVirtualScroll } from 'vuetify/components/VVirtualScroll';
    import {
        mdiFormatListBulletedSquare,
        mdiArrowExpandAll,
        mdiArrowCollapseAll,
        mdiMapMarkerRight,
        mdiWindowClose,
        mdiFilterRemove
    } from '@mdi/js';
    import { useDisplay } from 'vuetify';
    import { watchDebounced } from '@vueuse/core'
    import { getSingleParam } from 'x1-base/utils';
    import { toTypedSchema } from '@vee-validate/valibot';
    import * as v from 'valibot';

    import type { WatchStopHandle } from 'vue';

    const retailerListPanel = ref(false);
    const elLocationsMap = shallowRef<InstanceType<typeof LocationsMap>>();
    const route = useRoute();
    const createGlobalAlert = useGlobalAlert();
    const useLocationsObj = useLocations();
    const useUiStateObj = useUiState();
    const { isMobile } = useIsMobile();
    const { sportGroupLabel, sortSportGroups } = useSportGroups();
    const isMapExpanded = ref(false);
    const useDisplayObj = useDisplay();
    const marketsFilterItems = ref<Map<string, GenericVuetifyListItem>>(new Map());
    const retailLocationsFiltered = ref<Location[]>([]);
    const retailLocationsFilter = ref<string>('');
    const boundLocationsUids = ref<string[]>([]);
    const panelSearchBox = ref<number | null>(0);
    const topMargin = ref<string>('0');

    const unWatchUiState = watch(
        useDisplayObj.name,
        () => {
            panelSearchBox.value = isMobile.value ? null : 0;
            topMargin.value = `${useUiStateObj.headerHeightPx}px`;
            resizeMap();
        },
        { immediate: true }
    );

    const mapDefaultHeight = '560px';
    const mapHeight = ref<string>(mapDefaultHeight);

    const prePopSearch = getPrePopSearch();
    const searchObj = ref<LocationsMapSearch>(prePopSearch);
    let unWatchRetailLocations: WatchStopHandle;

    const {
        handleSubmit,
        defineField,
        meta: formMeta,
        resetForm,
        controlledValues
    } = useForm({
        validationSchema: toTypedSchema(
            LocationsMapSearchSchema
        ),
        initialValues: {
            radius: prePopSearch.radius ?? 20,
            unit: prePopSearch.unit ?? 'mi',
            location: prePopSearch.location || null,
            markets: prePopSearch.markets || null
        }
    });

    const { getDefineFieldOptions } = useFormValidation();
    const vo = () => getDefineFieldOptions('vuetify');

    const [radius, radiusProps] = defineField('radius', vo());
    const [unit, unitProps] = defineField('unit', vo());
    const [location, locationProps] = defineField('location', vo());
    const [markets, marketsProps] = defineField('markets', vo());

    const itemsRadius = ref<SelectListItem[]>([
        {
            text: '20',
            value: 20
        },
        {
            text: '50',
            value: 50
        },
        {
            text: '100',
            value: 100
        }
    ]);

    const itemsUnit = ref<SelectListItem[]>([
        {
            text: 'miles',
            value: 'mi'
        },
        {
            text: 'kilometers',
            value: 'km'
        }
    ]);

    async function getRetailLocations(): Promise<Location[] | Error> {
        const response = await useLocationsObj.getRetailLocations();

        if (lo_isError(response)) {
            return response;
        }

        return response.data;
    }

    const {
        data: retailLocations
    } = useLazyAsyncData<Location[] | Error>(
        'keyRetailLocations',
        () => getRetailLocations(),
        {
            server: false
        }
    );

    unWatchRetailLocations = watch(
        retailLocations,
        (newVal, oldVal) => {
            if (lo_isError(retailLocations.value)) {
                return;
            }

            if (newVal && !oldVal) {
                retailLocationsFiltered.value = retailLocations.value ?? [];
                unWatchRetailLocations?.();
            }
        },
        { immediate: true }
    );

    const unWatchMarkets = watch(
        () => searchObj.value.markets,
        (newVal, oldVal) => {
            onFilterLocations(true);
        },
        { immediate: true, deep: true }
    );

    const unWatchSearchTerm = watchDebounced(
        retailLocationsFilter,
        () => onFilterLocations(),
        { debounce: 500 }
    );

    const locationsCount = computed<number>(() => {
        if (Array.isArray(retailLocations.value)) {
            return retailLocations.value.length;
        }

        return 0;
    });

    const isFiltering = computed<boolean>(() => !!filteredLocationsCount.value && filteredLocationsCount.value < locationsCount.value);
    const filteredLocationsCount = computed<number>(() => retailLocationsFiltered.value.length);

    function resizeMap() {
        if (import.meta.client && isMobile.value) {
            requestAnimationFrame(() => {
                const box = elLocationsMap.value?.$el.getBoundingClientRect() ?? null;
                const offset = 20;

                if (!lo_isNil(box)) {
                    const winHeight = window.innerHeight;
                    mapHeight.value = `${winHeight - box.top - offset}px`;
                }
            });
        }
    }

    const onSubmit = handleSubmit(async (values) => {
        searchObj.value = lo_mergeCompat({}, searchObj.value, values);
        const { unit, location, radius, markets } = searchObj.value;
        await navigateTo({ path: route.path, query: {
            u: unit,
            l: location,
            r: radius,
            m: markets
        }, replace: true });
    });

    async function onReset(e: Event): Promise<void> {
        const defaults = LocationsMapSearchModel.toPlainObject<LocationsMapSearch>();
        resetForm({
            values: {
                radius: defaults.radius ?? 20,
                unit: defaults.unit ?? 'mi',
                location: defaults.location || null,
                markets: defaults.markets || null
            }
        });
        searchObj.value = defaults;
        boundLocationsUids.value = [];
        await nextTick();
        onFilterLocations();
        await nextTick();
        navigateTo({ path: route.path, query: {}, replace: true });
    }

    let markerTimer: TimeoutId = null;

    function locationItemClick(location: Location) {
        markerTimer = setTimeout(() => {
            elLocationsMap.value?.onOpenMarker(location);
        }, 10);
    }

    function showLocationSearchError() {
        createGlobalAlert({
            text: 'Could not find location specified. Please try again.',
            show: true,
            type: 'error'
        });
    }

    function toggleFullScreen() {
        isMapExpanded.value = !isMapExpanded.value;

        if (isMapExpanded.value) {
            mapHeight.value = '100%';
        } else if (isMobile.value && !isMapExpanded.value) {
            resizeMap();
        } else {
            mapHeight.value = mapDefaultHeight;
        }
    }

    function onFilterLocations(isLocationsSearch?: Nilish<boolean>): void {
        let locations: Nullable<Location[]> = [];

        if (Array.isArray(retailLocations.value)) {
            const term = retailLocationsFilter.value;
            const boundLen = boundLocationsUids.value.length;
            const selectedMarkets = searchObj.value.markets;

            locations = retailLocations.value.filter((location: Location) => {
                const hasTerm = (term && term.length) ? Boolean(location.address?.canonical?.match(new RegExp(term, 'i'))?.length) : true;
                const isBound = (boundLen) ? boundLocationsUids.value.includes(location.uid) : true;
                const satisfiesMarkets = selectedMarkets ? (
                    location.sportGroups.length && location.sportGroups.includes(selectedMarkets)
                ) : true;

                return (hasTerm && isBound && satisfiesMarkets);
            });

            if (locations.length === retailLocations.value.length && isLocationsSearch) {
                locations = [];
            }
        }

        retailLocationsFiltered.value = locations;
    }

    function onBoundLocations(uids: Nilish<string[]>): void {
        boundLocationsUids.value = uids ?? [];
        onFilterLocations(true);
    }

    function getPrePopSearch(): LocationsMapSearch {
        const q = route.query;
        // TODO: Fix Type
        const obj = LocationsMapSearchModel.toPlainObject<any>();
        const radius = parseInt(getSingleParam(q, 'r') || obj.radius, 10) || 20;
        const unit = getSingleParam(q, 'u') || obj.unit;
        const location = getSingleParam(q, 'l') || obj.location || null;
        const markets = getSingleParam(q, 'm') || obj.markets || null;
        return Object.assign(obj, { radius, location, unit, markets });
    }

    onBeforeMount(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((geolocation: GeolocationPosition) => {
                if (geolocation) {
                    searchObj.value.geolocation = geolocation;
                }
            });
        }
    });

    onBeforeUnmount(() => {
        clearTimeout(markerTimer || 0);
        unWatchUiState();
        unWatchSearchTerm();
        unWatchRetailLocations?.();
        unWatchMarkets();
    });
</script>

<style lang="scss" scoped>
.list-retailers {
    margin: 0;
    padding: 0;

    .v-list-item {
        list-style: none;
        margin: 0;
        padding: 1rem 0.2rem;
        border-bottom: 1px solid rgb(var(--v-theme-accent-lighten-1));
    }
}

address {
    font-style: normal;
}

.action-buttons {
    gap: 1rem;
}

.show-list-btn {
    z-index: 1;
}

.store-locator-container {
    &.full-screen {
        background-color: rgb(var(--v-theme-surface));
        position: fixed;
        top: 0;
        left: 0;
        z-index: 99999;
        width: 100%;
        height: 100vh;
        height: 100dvh;
        padding: 1rem;
    }
}

:deep(.v-navigation-drawer) {
    top: 0 !important;
    height: 100% !important;
}
</style>
